import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { comics, categories } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);
  const categoryName = category?.name || "分类";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSEO({
    title: `${categoryName}韩国漫画 - 免费在线${categoryName}韩漫 | 韩漫屋`,
    description: `韩漫屋为您精选${categoryName}类韩国漫画，涵盖丰富多样的${categoryName}韩漫作品，全部免费在线阅读，画质清晰更新及时。`,
    keywords: `${categoryName}韩国漫画,${categoryName}韩漫,免费${categoryName}漫画,韩漫屋`,
    canonicalUrl: `https://hanmanwu.com/category/${slug}`,
    ogTitle: `${categoryName}韩国漫画 - 免费在线${categoryName}韩漫 | 韩漫屋`,
    ogDescription: `韩漫屋为您精选${categoryName}类韩国漫画，全部免费在线阅读。`,
    ogUrl: `https://hanmanwu.com/category/${slug}`,
    ogType: "website",
    twitterCard: "summary_large_image",
    schemaObjects: [{
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${categoryName}韩国漫画 - 韩漫屋`,
      description: `韩漫屋${categoryName}分类下的韩国漫画列表`,
      url: `https://hanmanwu.com/category/${slug}`,
      isPartOf: {
        "@type": "WebSite",
        name: "韩漫屋",
        url: "https://hanmanwu.com",
      },
    }],
  });

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: category.name },
            ]}
          />

          <div className="py-8 md:py-12">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}韩国漫画
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}，共 {categoryComics.length} 部作品</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  cat.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                }`}
              >
                <i className={`${cat.icon} mr-1.5 w-3 h-3 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 pb-14" data-product-shop>
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-400"></i>
              <p className="text-foreground-600 mt-4">该分类下暂无漫画作品</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}